import { z } from 'zod';

/**
 * Closed set of machine-readable macro-step failures.
 * Native Playjector emits the first six; the last three are minted only by
 * the host (`PlayjectorService`) and never appear on the native wire.
 */
export const MACRO_STEP_ERROR_CODES = [
  'no_process',
  'process_not_ready',
  'no_hashes',
  'no_variants',
  'invalid_variant',
  'capture_failed',
  'request_in_flight',
  'reply_timeout',
  'abandoned',
] as const;

export type MacroStepErrorCode = (typeof MACRO_STEP_ERROR_CODES)[number];

/** Subset Playjector may put in `reply["errorCode"]`. Byte-identical to C++ `ToWireString`. */
export const NATIVE_MACRO_STEP_ERROR_CODES = [
  'no_process',
  'process_not_ready',
  'no_hashes',
  'no_variants',
  'invalid_variant',
  'capture_failed',
] as const;

export type NativeMacroStepErrorCode = (typeof NATIVE_MACRO_STEP_ERROR_CODES)[number];

export const macroStepErrorCodeSchema = z.enum(MACRO_STEP_ERROR_CODES);

/**
 * Version-skew bridge ONLY. An older Playjector sends prose with no `errorCode`.
 * Ordered, first-match-wins, and matched against the raw native `reply.error`
 * string — never against a wrapped/decorated Error message.
 */
export const LEGACY_MACRO_STEP_ERROR_PROSE: ReadonlyArray<
  readonly [prose: string, code: MacroStepErrorCode]
> = [
  ['process not ready', 'process_not_ready'],
  ['no process', 'no_process'],
  ['no hashes', 'no_hashes'],
  ['no variants', 'no_variants'],
  ['invalid variant', 'invalid_variant'],
  ['already in flight', 'request_in_flight'],
  ['abandoned', 'abandoned'],
  ['macro step reply timed out', 'reply_timeout'],
];

/** Returns undefined when no legacy mapping applies, so callers must fail closed. */
export function macroStepErrorCodeFromLegacyProse(
  nativeError: string,
): MacroStepErrorCode | undefined {
  const haystack = nativeError.toLowerCase();
  for (const [prose, code] of LEGACY_MACRO_STEP_ERROR_PROSE) {
    if (haystack.includes(prose)) {
      return code;
    }
  }
  return undefined;
}
